/*
* Shared configuration for the transaction list views whose ag-grid column filters are resolved by
* the API instead of by the grid. Both the column defs (AgGridTransactionsColumns) and the grid
* component (TransactionsConfList) need to agree on which routes those are: the column defs have to
* stop evaluating the filters locally, and the grid has to turn the filter model into a query.
*/

// Vuex actions used to refresh a list view whenever its filters or sorting change
const SERVER_FILTER_ROUTES = {
  APs: {
    fetchAction: 'fetchApsWithQuery',
    clearAction: 'clearApState',
    resetMetaAction: 'resetApMeta',
    filterInfoAction: 'setApFilterInfo'
  },
  PCs: {
    fetchAction: 'fetchPcsWithQuery',
    clearAction: 'clearPcState',
    resetMetaAction: 'resetPcMeta',
    filterInfoAction: 'setPcFilterInfo'
  },
  JEs: {
    fetchAction: 'fetchJesWithQuery',
    clearAction: 'clearJeState',
    resetMetaAction: 'resetJeMeta',
    filterInfoAction: 'setJeFilterInfo'
  },
  PRs: {
    fetchAction: 'fetchJesWithQuery',
    clearAction: 'clearJeState',
    resetMetaAction: 'resetJeMeta',
    filterInfoAction: 'setPrFilterInfo'
  },
  POs: {
    fetchAction: 'fetchPosWithQuery',
    clearAction: 'clearPoState',
    resetMetaAction: 'resetPoMeta',
    filterInfoAction: 'setPoFilterInfo'
  },
  Payments: {
    fetchAction: 'fetchPaymentsWithQuery',
    clearAction: 'clearPaymentState',
    resetMetaAction: 'resetPaymentMeta',
    filterInfoAction: 'setFilter'
  },
  PaymentProcessing: {
    fetchAction: 'fetchPaymentsApsWithQuery',
    clearAction: 'clearApState',
    resetMetaAction: 'resetApMeta',
    filterInfoAction: 'setFilter'
  },
  CheckProcessing: {
    fetchAction: 'fetchPrintableApsWithQuery',
    clearAction: 'clearApState',
    resetMetaAction: 'resetApMeta',
    filterInfoAction: 'setFilter'
  },
  Checks: {
    fetchAction: 'fetchChecksWithQuery',
    clearAction: 'clearCheckState',
    resetMetaAction: 'resetCheckMeta',
    filterInfoAction: 'setFilter'
  }
}

/**
 * Query params owned by the grid's column filters. They are rebuilt from the filter model on every
 * change, so stale values have to be dropped from the store filters before the new query is set
 * (SET_FILTERS merges rather than replaces).
 */
export const GRID_FILTER_PARAMS = [
  'page',
  'search',
  'id__in',
  'ledger__in',
  'number__icontains',
  'description__icontains',
  'amount_decimal__icontains',
  'payment_confirmation_number__icontains',
  'status__in',
  // The vendor column asks by id on the views whose API reads it that way, and by list on the rest
  'vendor',
  'vendor__in',
  'source_code__code__in',
  'batch',
  'batch__in',
  'batch__bank',
  'batch__bank__in',
  'batch__bank__currency__in',
  'payment_type__in',
  'payment_processed',
  'is_notified__in',
  'date__gte',
  'date__lte',
  'date_due__gte',
  'date_due__lte',
  'due_date__gte',
  'due_date__lte'
]

// Statuses offered by the status filter when the view has not scoped itself to a subset
export const DEFAULT_STATUS_VALUES = ['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'CLOSED', 'VOIDED']

// Statuses a payment goes through, which are not the ones the transaction views work in
export const PAYMENT_LIST_STATUS_VALUES = ['DRAFT', 'POSTED', 'VOIDED', 'CLOSED', 'DELETED']

// A posted payment is a printed one to the people reading the list
export const PAYMENT_LIST_STATUS_LABELS = { POSTED: 'PRINTED' }

// Statuses a check goes through, which are not the ones the transaction views work in
export const CHECK_LIST_STATUS_VALUES = ['DRAFT', 'POSTED', 'VOIDED', 'DELETED']

/**
 * Source codes the view does not ask for until the user ticks them. They are held as voided
 * journals, so ticking either is also asking for the VOIDED status.
 */
export const VOIDED_SOURCE_CODES = ['DC', 'VX']

// Source codes no source code filter offers, because the payroll journals are a list of their own
export const EXCLUDED_SOURCE_CODES = ['PR']

/**
 * The source codes a filter offers, out of the ones the store holds. The column defs and the grid
 * are both read against this: a code the filter never offers cannot be ticked, so a query built
 * from what it is asking for must not carry it either.
 * @param sourceCodes   The codes held in the store
 * @return {Array}
 */
export function offeredSourceCodes (sourceCodes) {
  return (sourceCodes || []).filter(code => code && !EXCLUDED_SOURCE_CODES.includes(code))
}

/**
 * Values a set filter starts with unticked, because the view does not ask for those rows until
 * they are. Keyed by column, and only applied on the views that have the column.
 */
export const DEFAULT_UNTICKED_VALUES = {
  status: ['VOIDED', 'DELETED'],
  source_code: VOIDED_SOURCE_CODES
}

const isBlankValue = (value) => value === null || value === undefined || !String(value).trim()

/**
 * The values a set filter is asking the API for, i.e. what is ticked less anything blank - the rows
 * a column has no value for are the API's to leave in, since there is nothing to ask it for them by
 * @param values   The values the filter is asking for
 * @return {Array}
 */
export function pickedValues (values) {
  return (values || []).filter(value => !isBlankValue(value))
}

/**
 * Whether the given source codes cover the journals the API holds as voided ones
 * @param sourceCodes   The source codes a filter is asking for
 * @return {boolean}
 */
export function includesVoidedSourceCodes (sourceCodes) {
  return (sourceCodes || []).some(code => VOIDED_SOURCE_CODES.includes(code))
}

/**
 * Columns whose set filter opens on something other than every value ticked. Besides the ones with
 * values left out above, payment processing opens on its default bank.
 */
export const SEEDED_FILTER_COLUMNS = ['source_code', 'status', 'bank']

/**
 * The statuses a view's status filter offers. A payment goes through the same ones wherever it is
 * read, so the whole of them are offered and the view's own criteria decide which start ticked
 * rather than which are there at all.
 * @param routeName
 * @param baseQuery   The view's own criteria, whose statuses scope the ones on offer
 * @return {Array}
 */
export function statusFilterValues (routeName, baseQuery) {
  if (routeName === 'Payments') return PAYMENT_LIST_STATUS_VALUES
  if (routeName === 'Checks') return CHECK_LIST_STATUS_VALUES
  const scopedStatuses = baseQuery && baseQuery.status__in
  return (Array.isArray(scopedStatuses) && scopedStatuses.length) ? scopedStatuses : DEFAULT_STATUS_VALUES
}

/**
 * What the payment status filter offers, against the flag the API stores it as. The filter is read
 * and picked from as Cleared/Pending; the query is the boolean behind it.
 */
export const PAYMENT_STATUS_VALUES = {
  PAID: true,
  UNPAID: false
}

/**
 * Returns the actions used to refresh the given route, or null when the route still filters
 * client side (Checks, Payments, CheckProcessing, PaymentProcessing).
 * @param routeName
 * @return {Object|null}
 */
export function getServerFilterConfig (routeName) {
  return SERVER_FILTER_ROUTES[routeName] || null
}

/**
 * Whether the given route resolves its ag-grid column filters through the API
 * @param routeName
 * @return {boolean}
 */
export function isServerFiltered (routeName) {
  return !!getServerFilterConfig(routeName)
}

/**
 * Whether the route is a single transaction's edit view. That is the one departure a list view
 * keeps its filters for: the user went to look at a row of the list they had filtered, and coming
 * back should land them where they left off.
 * @param routeName
 * @return {boolean}
 */
export function isTransactionEditRoute (routeName) {
  return typeof routeName === 'string' && routeName.indexOf('Edit') === 0
}

/*
* Where a list view's column filters wait out the trip to a transaction. The grid is destroyed when
* the view is left, so what the user filtered by has to outlive it: it is written on the way into a
* transaction and read back - once - when the list is opened again.
*/
const FILTER_MODEL_STORAGE_KEY = 'gridFilterModel'

const filterModelKey = (routeName) => `${FILTER_MODEL_STORAGE_KEY}:${routeName}`

/**
 * Keeps a view's column filters and the page they were read on. The page is kept whether or not
 * anything was filtered: a list read to its third page is where the user left off just as much as
 * a filtered one, and a set filter with every value ticked leaves no model behind either.
 * @param routeName
 * @param filterModel   ag-grid filter model
 * @param page          The page the list was on
 */
export function saveFilterModel (routeName, filterModel, page = 1, extendedFilters = {}) {
  if (!routeName) return
  try {
    sessionStorage.setItem(filterModelKey(routeName), JSON.stringify({ model: filterModel || {}, page, extendedFilters }))
  } catch (error) {
    console.log(error)
  }
}

/**
 * The column filters a view was left with on the way into a transaction
 * @param routeName
 * @return {Object|null}  { model, page }
 */
export function readFilterModel (routeName) {
  if (!routeName) return null
  try {
    const saved = sessionStorage.getItem(filterModelKey(routeName))
    return saved ? JSON.parse(saved) : null
  } catch (error) {
    console.log(error)
    return null
  }
}

/**
 * Forgets a view's filters and the page they were read on, so the next visit starts on the view's
 * own criteria at the head of the list
 * @param routeName
 */
export function clearFilterModel (routeName) {
  if (!routeName) return
  try {
    sessionStorage.removeItem(filterModelKey(routeName))
  } catch (error) {
    console.log(error)
  }
}

/**
 * Settles what a list view is entered with, before its grid is built. Where the user left off -
 * their filters and the page they were reading - is kept only for a return trip from one of the
 * view's own transactions; arriving from anywhere else drops it, so the grid finds nothing to put
 * back and the view loads the head of the list on its own criteria.
 * @param toRouteName     The list view being entered
 * @param fromRouteName   The route being left
 * @return {boolean}      Whether the grid has somewhere to pick up from
 */
export function retainFiltersOnEntry (toRouteName, fromRouteName) {
  if (isTransactionEditRoute(fromRouteName)) return !!readFilterModel(toRouteName)
  clearFilterModel(toRouteName)
  return false
}



// WEBPACK FOOTER //
// ./src/Mixins/SigmaClientNew/transactionGridFilters.js